import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CollectionSearchQuery } from 'src/api/functions/collectionsApi';

export const COLLECTION_PAGE_SIZE = 30;

/** Prefix used for schema/custom-data field filters in the URL, e.g. `f.status=approved`. */
const FIELD_PREFIX = 'f.';

export type CollectionFilterChip = {
  /** URL param name (e.g. `q`, `content_type`, `f.status`). */
  key: string;
  /** Human-readable label rendered in the chip. */
  label: string;
};

export interface CollectionFiltersState {
  /** Ready-to-send search body for `POST /assetcollection/{id}/search`. */
  query: CollectionSearchQuery;
  /** 1-based current page. */
  page: number;
  /** True when any asset filter (not just paging) is active. */
  hasFilters: boolean;
  /** Active filter chips (excludes pagination). */
  chips: CollectionFilterChip[];
  setPage: (page: number) => void;
  /** Remove a single filter by its URL param key (resets to page 1). */
  removeFilter: (key: string) => void;
  /** Clear all filters and paging. */
  clearFilters: () => void;
}

/**
 * Parses the collection deep-link URL params into a schema-aware search
 * query and keeps them in the URL so a filtered view is shareable and
 * survives reload.
 *
 * Supported params:
 *  - `q`            free-text keyword
 *  - `schema`       data_schema_id (required to filter schema fields)
 *  - `f.<field>`    schema/custom-data field filter, repeatable (e.g. `f.status=approved`)
 *  - `content_type` core content type prefix (image, video, ...)
 *  - `sort`         e.g. `upload_time:desc`
 *  - `page`         1-based page number
 */
export function useCollectionFilters(): CollectionFiltersState {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const keyword = params.get('q') || undefined;
  const contentType = params.get('content_type') || undefined;
  const sortBy = params.get('sort') || undefined;
  const schemaParam = params.get('schema');
  const dataSchemaId = schemaParam ? parseInt(schemaParam, 10) : undefined;

  const pageParam = parseInt(params.get('page') || '1', 10);
  const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;

  const fields = useMemo(() => {
    const out: Record<string, string> = {};
    params.forEach((value, key) => {
      if (key.startsWith(FIELD_PREFIX) && value) {
        out[key.slice(FIELD_PREFIX.length)] = value;
      }
    });
    return out;
  }, [params]);

  const query = useMemo<CollectionSearchQuery>(() => {
    const q: CollectionSearchQuery = {
      limit: COLLECTION_PAGE_SIZE,
      offset: (page - 1) * COLLECTION_PAGE_SIZE,
    };
    if (keyword) q.keyword = keyword;
    if (contentType) q.content_type = contentType;
    if (sortBy) q.sort_by = sortBy;
    const hasFields = Object.keys(fields).length > 0;
    if ((dataSchemaId !== undefined && !Number.isNaN(dataSchemaId)) || hasFields) {
      q.schema_criteria = {
        ...(dataSchemaId !== undefined && !Number.isNaN(dataSchemaId) ? { data_schema_id: dataSchemaId } : {}),
        ...(hasFields ? { fields } : {}),
      };
    }
    return q;
  }, [keyword, contentType, sortBy, dataSchemaId, fields, page]);

  const chips = useMemo<CollectionFilterChip[]>(() => {
    const out: CollectionFilterChip[] = [];
    if (keyword) out.push({ key: 'q', label: `Search: ${keyword}` });
    if (contentType) out.push({ key: 'content_type', label: `Type: ${contentType}` });
    Object.entries(fields).forEach(([field, value]) => {
      out.push({ key: `${FIELD_PREFIX}${field}`, label: `${field}: ${value}` });
    });
    if (sortBy) out.push({ key: 'sort', label: `Sort: ${sortBy}` });
    return out;
  }, [keyword, contentType, sortBy, fields]);

  const hasFilters = chips.length > 0;

  const replaceParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(location.search);
      mutate(next);
      history.replace({ pathname: location.pathname, search: next.toString() });
    },
    [history, location.pathname, location.search],
  );

  const setPage = useCallback(
    (next: number) =>
      replaceParams((p) => {
        if (next > 1) p.set('page', String(next));
        else p.delete('page');
      }),
    [replaceParams],
  );

  const removeFilter = useCallback(
    (key: string) =>
      replaceParams((p) => {
        p.delete(key);
        p.delete('page');
      }),
    [replaceParams],
  );

  const clearFilters = useCallback(
    () =>
      replaceParams((p) => {
        Array.from(p.keys()).forEach((key) => {
          if (key === 'q' || key === 'content_type' || key === 'sort' || key === 'schema' || key.startsWith(FIELD_PREFIX) || key === 'page') {
            p.delete(key);
          }
        });
      }),
    [replaceParams],
  );

  return { query, page, hasFilters, chips, setPage, removeFilter, clearFilters };
}
